/**
 * The workspace's call (HuddleViewModel), provided by the app shell next to WorkspaceContext.
 * Native: an `@Environment` value / `CompositionLocal` holding the same view model.
 */
import { Features, Roles, type HuddlesViewModel, type HuddleViewModel, type HuddleViewState, type WorkspaceState } from "@bundle/core";
import { createContext, useContext, useSyncExternalStore } from "react";
import { WorkspaceContext } from "./AppContext.tsx";

export type HuddleContextValue = {
  vm: HuddleViewModel;
  /**
   * This client can't carry calls itself (the Linux desktop app: WebKitGTK has no WebRTC). Join buttons then call
   * `openInBrowser` instead of joining.
   */
  browserOnly: boolean;
  /** Opens the conversation's huddle in the system browser (only used when `browserOnly`). */
  openInBrowser: (channelId: string) => void;
  /** The app's web origin: "Join with a link or code" opens links from elsewhere on their page there. */
  webOrigin?: string;
  /** The Huddles area (absent on the public meeting page). */
  area?: HuddlesViewModel;
};

export const HuddleContext = createContext<HuddleContextValue | null>(null);

/** Whether an admin switched Huddles off for me (core logic/features): every call entry point then hides. */
function huddlesHidden(ws: WorkspaceState | null, now: number): boolean {
  const cache = ws?.cache;
  if (cache === undefined) return false;
  const me = cache.members[ws!.meId];
  const manager = me !== undefined && Roles.isManager(me, now);
  return Features.featureAccess(cache.workspace.features, "huddles", manager, cache.workspace.featureAdminAccess) === "none";
}

/** The call view model and its state (null outside a workspace, or while Huddles is switched off for me). */
export function useHuddle(): (HuddleContextValue & { state: HuddleViewState }) | null {
  const ctx = useContext(HuddleContext);
  const state = useSyncExternalStore(
    ctx?.vm.subscribe ?? noopSubscribe,
    ctx?.vm.getSnapshot ?? noSnapshot,
    ctx?.vm.getSnapshot ?? noSnapshot,
  );
  const ws = useContext(WorkspaceContext)?.workspace;
  const wsState = useSyncExternalStore(ws?.subscribe ?? noopSubscribe, ws?.getSnapshot ?? noWorkspace, ws?.getSnapshot ?? noWorkspace);
  if (ctx === null || state === null || (ws !== undefined && huddlesHidden(wsState, ws.deps.clock.now()))) return null;
  return { ...ctx, state };
}

const noopSubscribe = () => () => {};
const noSnapshot = (): HuddleViewState | null => null;
const noWorkspace = (): WorkspaceState | null => null;
